import { rtkApi, GET_APPLICANTS, GET_APPLICATIONS, GET_APPLICATIONS_COUNT, GET_APPLICATION, GET_STUDENTS, GET_STUDENT } from '@/shared/api/rtkApi';
import {
  ApplicantsResponse,
  ApplicantsParams,
  ApproveApplicantResponse,
  UpdateApplicantStateRequest,
  UpdateApplicantStateResponse,
  DeleteApplicantResponse,
  BulkDeleteApplicantsRequest,
  BulkDeleteApplicantsResponse,
  ApplicantsPagination,
  ApplicationsParams,
  ApplicationsResponse,
  ApplicationListItem,
  ApproveApplicationResponse,
  ApproveApplicationRequest,
  UpdateApplicationRequest,
  UpdateApplicationResponse,
  DeleteApplicationResponse,
  ApplicationsCountResponse,
  ProfileCorrectionRequest,
  ProfileCorrectionResponse,
} from './types';

export const applicantsApi = rtkApi.injectEndpoints({
  endpoints: (build) => ({
    getApplicants: build.query<ApplicantsResponse, ApplicantsParams>({
      query: (params) => ({
        url: '/admin/applicants',
        params,
      }),
      providesTags: [GET_APPLICANTS],
    }),
    getApplications: build.query<
      ApplicationsResponse & {
        data: { items: ApplicationListItem[]; pagination: ApplicantsPagination };
      },
      ApplicationsParams
    >({
      query: (params) => ({
        url: '/admin/applications',
        params,
      }),
      transformResponse: (response: ApplicationsResponse) => ({
        ...response,
        data: {
          ...response.data,
          items: response.data.items.map((item) => ({
            ...item,
            id: item.application.id,
            profileCorrection: item.profileCorrection ?? null,
          })),
        },
      }),
      providesTags: [GET_APPLICATIONS],
    }),
    getApplicationsCount: build.query<ApplicationsCountResponse, void>({
      query: () => ({
        url: '/admin/applications/count',
      }),
      providesTags: [GET_APPLICATIONS_COUNT],
    }),
    approveApplication: build.mutation<
      ApproveApplicationResponse,
      { applicationId: number; body: ApproveApplicationRequest }
    >({
      query: ({ applicationId, body }) => ({
        url: `/admin/applications/${applicationId}/approve`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [GET_APPLICATIONS, GET_APPLICATIONS_COUNT, GET_APPLICATION, GET_STUDENTS],
    }),
    updateApplication: build.mutation<
      UpdateApplicationResponse,
      { applicationId: number; body: UpdateApplicationRequest }
    >({
      query: ({ applicationId, body }) => ({
        url: `/admin/applications/${applicationId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [GET_APPLICATIONS, GET_APPLICATIONS_COUNT, GET_APPLICATION],
    }),
    deleteApplication: build.mutation<DeleteApplicationResponse, { applicationId: number }>({
      query: ({ applicationId }) => ({
        url: `/admin/applications/${applicationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        GET_APPLICATIONS,
        GET_APPLICATIONS_COUNT,
        GET_APPLICATION,
        GET_STUDENTS,
        GET_STUDENT,
      ],
    }),
    approveApplicant: build.mutation<ApproveApplicantResponse, { enrollmentId: number }>({
      query: ({ enrollmentId }) => ({
        url: `/admin/applicants/${enrollmentId}/approve`,
        method: 'PUT',
      }),
      invalidatesTags: [GET_APPLICANTS, GET_STUDENTS],
    }),
    updateApplicantState: build.mutation<
      UpdateApplicantStateResponse,
      { enrollmentId: number; body: UpdateApplicantStateRequest }
    >({
      query: ({ enrollmentId, body }) => ({
        url: `/admin/applicants/${enrollmentId}/state`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [GET_APPLICANTS],
    }),
    deleteApplicant: build.mutation<DeleteApplicantResponse, { enrollmentId: number }>({
      query: ({ enrollmentId }) => ({
        url: `/admin/applicants/${enrollmentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [GET_APPLICANTS],
    }),
    bulkDeleteApplicants: build.mutation<BulkDeleteApplicantsResponse, BulkDeleteApplicantsRequest>(
      {
        query: (body) => ({
          url: '/admin/applicants/bulk-delete',
          method: 'POST',
          body,
        }),
        invalidatesTags: [GET_APPLICANTS],
      },
    ),
    requestProfileCorrection: build.mutation<
      ProfileCorrectionResponse,
      { userId: number; body: ProfileCorrectionRequest }
    >({
      query: ({ userId, body }) => ({
        url: `/admin/students/accounts/${userId}/profile-correction`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        GET_APPLICATIONS,
        GET_APPLICATIONS_COUNT,
        GET_APPLICATION,
        GET_STUDENTS,
        GET_STUDENT,
      ],
    }),
  }),
});

export const {
  useGetApplicantsQuery,
  useGetApplicationsQuery,
  useGetApplicationsCountQuery,
  useApproveApplicationMutation,
  useUpdateApplicationMutation,
  useDeleteApplicationMutation,
  useApproveApplicantMutation,
  useUpdateApplicantStateMutation,
  useDeleteApplicantMutation,
  useBulkDeleteApplicantsMutation,
  useRequestProfileCorrectionMutation,
} = applicantsApi;
